// src/pages/RaiseTicketPage.jsx
//
// Single-column form, max-width 760px. Form rows are a 180px / 1fr grid:
// label column on the left, input column on the right.
//
// Rows: Ticket type (3 cards), Property (+ unit), Priority (4 segmented),
//       Requester (name + email/unit), Title, Description, Attachments.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  useTheme,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import Layout from "../components/layout/Layout";
import FormRow, { BrandFormCard } from "../components/brand/FormRow";
import { fonts } from "../theme/tokens";
import { useAuth } from "../context/AuthContext";
import { createTicket } from "../api/ticketApi";
import { getAccessibleProperties, getPropertyLPE1Forms, searchProperties } from "../api/propertyApi";

const TYPES = [
  {
    key: "Maintenance",
    label: "Maintenance",
    desc: "Repairs, lifts, lighting, fobs, leaks",
    Icon: BuildRoundedIcon,
  },
  {
    key: "Major Works",
    label: "Major Works",
    desc: "Section 20, façade, roof, larger projects",
    Icon: HandymanRoundedIcon,
  },
  {
    key: "Management Pack",
    label: "Management Pack",
    desc: "LPE1, LPE2, sale enquiries",
    Icon: DescriptionRoundedIcon,
  },
];

const PRIORITIES = ["Low", "Medium", "High", "Urgent"];

export default function RaiseTicketPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [type, setType] = useState("Maintenance");
  const [priority, setPriority] = useState("Medium");
  const [property, setProperty] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [propertyInputValue, setPropertyInputValue] = useState("");
  const [unit, setUnit] = useState("");
  const [propertyEnquiryForm, setPropertyEnquiryForm] = useState(null);
  const [propertyEnquiryOptions, setPropertyEnquiryOptions] = useState([]);
  const [propertyEnquiryLoading, setPropertyEnquiryLoading] = useState(false);
  const [requesterName, setRequesterName] = useState("");
  const [requesterContact, setRequesterContact] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { id, type, propertyName }
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.name && !requesterName) setRequesterName(user.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const mapPropertyOptions = (properties) =>
    (properties || []).map((p) => ({
      id: p.property_id,
      name: p.property_name,
      address_line1: p.address_line1,
      city: p.city,
    }));

  const mapPropertyEnquiryOptions = (forms) =>
    (forms || []).map((form) => {
      const formType = form?.form_type || "LPE1";
      const unitLabel = form?.unit_name || `Unit ${form?.unit_id ?? "-"}`;
      return {
        id: form.id,
        form_type: formType,
        unit_id: form.unit_id,
        unit_name: unitLabel,
        label: `${formType} · ${unitLabel} · #${form.id}`,
      };
    });

  const loadLiveProperties = useCallback(async () => {
    setPropertyLoading(true);
    try {
      const results = await getAccessibleProperties(true);
      setPropertyOptions(mapPropertyOptions(results));
    } catch (e) {
      console.error("Live property load failed", e);
      setPropertyOptions([]);
    } finally {
      setPropertyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLiveProperties();
  }, [loadLiveProperties]);

  useEffect(() => {
    let ignore = false;

    const loadPropertyEnquiries = async () => {
      if (type !== "Management Pack") {
        setPropertyEnquiryForm(null);
        setPropertyEnquiryOptions([]);
        setPropertyEnquiryLoading(false);
        return;
      }

      if (!property?.id) {
        setPropertyEnquiryForm(null);
        setPropertyEnquiryOptions([]);
        return;
      }

      setPropertyEnquiryLoading(true);
      try {
        const forms = [];
        let page = 1;
        let totalPages = 1;

        while (page <= totalPages) {
          const data = await getPropertyLPE1Forms(property.id, {
            page,
            page_size: 100,
          });
          forms.push(...(data?.results || []));
          totalPages = data?.total_pages || 1;
          page += 1;
        }

        if (ignore) return;

        const nextOptions = mapPropertyEnquiryOptions(forms);
        setPropertyEnquiryOptions(nextOptions);
        setPropertyEnquiryForm((currentValue) => {
          if (!currentValue) return null;
          return (
            nextOptions.find(
              (option) =>
                option.id === currentValue.id && option.form_type === currentValue.form_type,
            ) || null
          );
        });
      } catch (e) {
        console.error("Property enquiry form load failed", e);
        if (!ignore) {
          setPropertyEnquiryOptions([]);
          setPropertyEnquiryForm(null);
        }
      } finally {
        if (!ignore) {
          setPropertyEnquiryLoading(false);
        }
      }
    };

    loadPropertyEnquiries();

    return () => {
      ignore = true;
    };
  }, [property, type]);

  const handlePropertySearch = async (q) => {
    const query = (q || "").trim();
    if (!query) {
      loadLiveProperties();
      return;
    }
    setPropertyLoading(true);
    try {
      const results = await searchProperties(query, 20, true);
      setPropertyOptions(mapPropertyOptions(results));
    } catch (e) {
      console.error("Property search failed", e);
      setPropertyOptions([]);
    } finally {
      setPropertyLoading(false);
    }
  };

  const validate = () => {
    if (!type) return "Choose a ticket type.";
    if (!property) return "Pick a property.";
    if (type === "Management Pack" && !propertyEnquiryForm) {
      return "Pick a property enquiry form.";
    }
    if (!priority) return "Pick a priority.";
    if (!title.trim()) return "Add a short title.";
    if (!description.trim()) return "Add a description.";
    return "";
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim() + (unit.trim() ? ` (Unit ${unit.trim()})` : ""),
        description: description.trim(),
        priority,
        status: "Open",
        type,
        requester_name: requesterName.trim(),
        property_id: property.id,
      };
      if (type === "Management Pack" && propertyEnquiryForm) {
        payload.property_enquiry_form_type = propertyEnquiryForm.form_type;
        payload.property_enquiry_form_id = propertyEnquiryForm.id;
      }
      if (requesterContact.trim()) {
        payload.description = `${payload.description}\n\nContact: ${requesterContact.trim()}`;
      }
      const created = await createTicket(payload);
      setSubmitted({
        id: created?.id || created?.ticket?.id || "—",
        type,
        propertyName: property.name,
      });
    } catch (e) {
      console.error("Ticket create failed", e);
      setError("Couldn't create the ticket. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(null);
    setTitle("");
    setDescription("");
    setUnit("");
    setPropertyEnquiryForm(null);
    setError("");
  };

  // SUCCESS STATE
  if (submitted) {
    return (
      <Layout
        eyebrow={`Reference · #${submitted.id}`}
        title="Ticket created"
      >
        <Box
          sx={{
            maxWidth: 560,
            mx: "auto",
            mt: 3,
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.brand.border}`,
            borderRadius: "8px",
            p: 4.5,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: theme.palette.brand.goldSoft,
              color: theme.palette.brand.goldDeep,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.25,
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 26,
              fontWeight: 600,
              m: 0,
              mb: 1,
            }}
          >
            Filed on the queue.
          </Typography>
          <Typography
            sx={{
              color: theme.palette.brand.inkMuted,
              fontSize: 14,
              lineHeight: 1.6,
              m: 0,
              mb: 3,
            }}
          >
            Your <strong>{submitted.type}</strong> ticket for{" "}
            <strong>{submitted.propertyName}</strong> has been recorded.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.25, justifyContent: "center" }}>
            <Button variant="outlined" onClick={reset}>
              Raise another
            </Button>
            <Button variant="contained" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </Box>
        </Box>
      </Layout>
    );
  }

  // FORM
  return (
    <Layout eyebrow="New ticket" title="Raise a ticket">
      <Box sx={{ maxWidth: 760 }}>
        <BrandFormCard sx={{ p: { xs: 3, md: "28px 32px" } }}>
          {/* TICKET TYPE */}
          <FormRow label="Ticket type" hint="Categorisation drives routing.">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1.25,
              }}
            >
              {TYPES.map((c) => {
                const sel = type === c.key;
                const Icon = c.Icon;
                return (
                  <Box
                    key={c.key}
                    onClick={() => setType(c.key)}
                    sx={{
                      p: "14px 12px",
                      backgroundColor: sel ? theme.palette.brand.black : theme.palette.background.paper,
                      border: `1px solid ${sel ? theme.palette.brand.black : theme.palette.brand.border}`,
                      borderRadius: "6px",
                      cursor: "pointer",
                      textAlign: "left",
                      color: sel ? "#fff" : theme.palette.brand.ink,
                      transition: "all .12s",
                      "&:hover": { borderColor: sel ? theme.palette.brand.black : theme.palette.brand.borderStrong },
                    }}
                  >
                    <Icon
                      sx={{
                        fontSize: 18,
                        color: sel ? theme.palette.brand.gold : theme.palette.brand.inkMuted,
                        mb: 1,
                      }}
                    />
                    <Box sx={{ fontSize: 14, fontWeight: 600, mb: 0.25 }}>{c.label}</Box>
                    <Box
                      sx={{
                        fontSize: 11.5,
                        color: sel ? "rgba(255,255,255,0.65)" : theme.palette.brand.inkSoft,
                        lineHeight: 1.4,
                      }}
                    >
                      {c.desc}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </FormRow>

          {/* PROPERTY */}
          <FormRow label="Property" hint="Which building does this relate to?">
            <Autocomplete
              options={propertyOptions}
              getOptionLabel={(o) => o?.name || ""}
              isOptionEqualToValue={(o, v) => o?.id === v?.id}
              value={property}
              inputValue={propertyInputValue}
              loading={propertyLoading}
              onOpen={() => {
                if (!propertyInputValue.trim() && propertyOptions.length === 0) {
                  loadLiveProperties();
                }
              }}
              onInputChange={(_, q) => {
                setPropertyInputValue(q);
                handlePropertySearch(q);
              }}
              onChange={(_, v) => setProperty(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search property…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {propertyLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                    {option.address_line1 && (
                      <Box sx={{ fontSize: 12, color: theme.palette.brand.inkSoft }}>
                        {option.address_line1}
                        {option.city ? `, ${option.city}` : ""}
                      </Box>
                    )}
                  </Box>
                </li>
              )}
            />
            <TextField
              size="small"
              fullWidth
              placeholder="Unit / flat (optional)"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              sx={{ mt: 1 }}
            />
            {type === "Management Pack" && (
              <Autocomplete
                options={propertyEnquiryOptions}
                getOptionLabel={(option) => option?.label || ""}
                isOptionEqualToValue={(option, value) => (
                  option?.id === value?.id && option?.form_type === value?.form_type
                )}
                value={propertyEnquiryForm}
                loading={propertyEnquiryLoading}
                disabled={!property}
                onChange={(_, value) => setPropertyEnquiryForm(value)}
                sx={{ mt: 1 }}
                noOptionsText={property ? "No property enquiries found." : "Select a property first."}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Property Enquiry Form"
                    placeholder="Select an LPE1 or FME1 questionnaire"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {propertyEnquiryLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </FormRow>

          {/* PRIORITY */}
          <FormRow label="Priority">
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 1,
              }}
            >
              {PRIORITIES.map((p) => {
                const sel = priority === p;
                return (
                  <Box
                    key={p}
                    onClick={() => setPriority(p)}
                    sx={{
                      p: "10px 8px",
                      textAlign: "center",
                      backgroundColor: sel ? theme.palette.brand.black : theme.palette.background.paper,
                      color: sel ? "#fff" : theme.palette.brand.ink,
                      border: `1px solid ${sel ? theme.palette.brand.black : theme.palette.brand.border}`,
                      borderRadius: "6px",
                      fontSize: 12.5,
                      fontWeight: 600,
                      cursor: "pointer",
                      transition: "all .12s",
                      "&:hover": {
                        borderColor: sel ? theme.palette.brand.black : theme.palette.brand.borderStrong,
                      },
                    }}
                  >
                    {p}
                  </Box>
                );
              })}
            </Box>
          </FormRow>

          {/* REQUESTER */}
          <FormRow label="Requester" hint="Who reported this? Optional.">
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
              <TextField
                size="small"
                placeholder="Name"
                value={requesterName}
                onChange={(e) => setRequesterName(e.target.value)}
              />
              <TextField
                size="small"
                placeholder="Email or unit"
                value={requesterContact}
                onChange={(e) => setRequesterContact(e.target.value)}
              />
            </Box>
          </FormRow>

          {/* TITLE */}
          <FormRow label="Title" hint="Short — one line.">
            <TextField
              size="small"
              fullWidth
              placeholder="e.g. Lift not working on car park level"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormRow>

          {/* DESCRIPTION */}
          <FormRow label="Description" hint="When it started, what's happening, any detail.">
            <TextField
              size="small"
              fullWidth
              multiline
              rows={5}
              placeholder="The lift stopped responding around 9:30 this morning…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormRow>

          {/* ATTACHMENTS */}
          <FormRow label="Attachments" hint="Optional. Up to 10 MB." last>
            <Box
              sx={{
                border: `1.5px dashed ${theme.palette.brand.borderStrong}`,
                borderRadius: "6px",
                p: "20px 16px",
                textAlign: "center",
                backgroundColor: theme.palette.brand.surfaceMuted,
                cursor: "not-allowed",
                opacity: 0.7,
              }}
            >
              <ImageOutlinedIcon
                sx={{ color: theme.palette.brand.inkSoft, fontSize: 22, mb: 0.5 }}
              />
              <Box sx={{ fontSize: 13, fontWeight: 500, color: theme.palette.brand.ink }}>
                Drop files here, or click to browse
              </Box>
              <Box sx={{ fontSize: 11.5, color: theme.palette.brand.inkSoft, mt: 0.5 }}>
                Coming soon — attach via the ticket detail view for now.
              </Box>
            </Box>
          </FormRow>
        </BrandFormCard>

        {error && (
          <Box
            sx={{
              mt: 2,
              fontSize: 13,
              color: theme.palette.error.main,
              textAlign: "right",
            }}
          >
            {error}
          </Box>
        )}

        <Box sx={{ display: "flex", gap: 1.25, justifyContent: "flex-end", pt: 2 }}>
          <Button variant="outlined" onClick={() => navigate("/tickets")}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            endIcon={!submitting && <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          >
            {submitting ? "Submitting…" : "Submit ticket"}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
}
